import { SCENE_COMMON_TOKEN, SceneCommon } from '@cgs/common';
import { ReelWinPosition, SpinResponse } from '@cgs/data-access';
import {
  BACKGROUND_MODE_COMPONENT_TOKEN,
  CollapseGameStateMachine,
  CollapseReelsEngine,
  ComponentNames,
  DrawOrderConstants,
  GAME_DATA_SERVICE_TOKEN,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameDataService,
  IFadeReelsProvider,
  IGameStateMachineProvider,
  IReelsConfig,
  IReelsConfigProvider,
  ISlotGameEngineProvider,
  ReelsSoundModel,
  RegularSpinsSoundModelComponent,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  T_IFadeReelsProvider,
  T_IReelsConfigProvider,
  T_ISlotGameEngineProvider,
  T_RegularSpinsSoundModelComponent,
  WaitForAction,
} from '@cgs/machines';
import { NodeUtils } from '@cgs/shared';
import { TRIFECTA_SDK_ADAPTER_TOKEN, TrifectaSDKAdapter } from '@cgs/provider-trifecta';
import {
  Button,
  ButtonBase,
  Compatibility,
  Container,
  debounce,
  easeInQuart,
  EmptyAction,
  EventStream,
  EventStreamSubscription,
  FunctionAction,
  InterpolateInplaceAction,
  IntervalAction,
  ParallelAction,
  Rect,
  SceneObject,
  SequenceAction,
  Vector2,
  viewportChangeListener,
} from '@cgs/wge';
import { SantaBackgroundImageComponent } from '../../../background-image/santa-background-image.component';
import {
  SANTA_MULTIPLIER_HOLDER_COMPONENT_TOKEN,
  SantaMultiplierHolderComponent,
} from '../../multiplier-holder/santa-multiplier-holder.component';

const enum SceneNames {
  Popup = 'slot/wheel/wheel',
  BackgroundPopup = 'slot/wheel/wheelBG',
  WheelBackground = 'WheelBG',
  WheelBackgroundAnim = 'wheelBG_Anim',
  Wheel = 'wheel',
  WheelAnim = 'wheelAnim',
  ButtonContinue = 'btnContinue',
  ButtonStates = 'btn_start_states',
  SizeHolderHorizontal = 'sizeHolder_Horizontal',
  AnimIconsHolder = 'anim_icons_holder',
}

const enum SceneStates {
  Show = 'show',
  Popup = 'popup',
  Hide = 'hide',
  On = 'on',
  Off = 'off',
}

const SCATTER_WIN_POSITION_TYPE = 'scatter';
const PICK_BONUS_ROUND = 0;

const POPUP_BACKGROUND_DRAW_ORDER = DrawOrderConstants.Top;
const POPUP_DRAW_ORDER = DrawOrderConstants.Top + 1;

export class SantaStartFreeSpinsPopupComponent {
  private readonly gameStateMachine: CollapseGameStateMachine<SpinResponse>;

  private readonly multiplierHolderComponent: SantaMultiplierHolderComponent;
  private readonly fadeReelsProvider: IFadeReelsProvider;
  protected readonly reelsEngine: CollapseReelsEngine;
  private readonly resourcesComponent: ResourcesComponent;
  private readonly reelsConfig: IReelsConfig;
  private popupScene: SceneObject;
  private backgroundScene: SceneObject;

  private backgroundImage: SceneObject;

  private slotBackgroundImageComponent: SantaBackgroundImageComponent;

  private scatterIcons: SceneObject[] = [];
  private scatterClickedIndex: number | null = null;
  private readonly reelSoundModel: ReelsSoundModel;
  private readonly trifectaSDKAdapter: TrifectaSDKAdapter | null;
  private readonly gameDataService: GameDataService;

  constructor(private container: Container) {
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine as CollapseGameStateMachine<SpinResponse>;

    this.fadeReelsProvider = container.resolve<IFadeReelsProvider>(T_IFadeReelsProvider);

    this.reelsEngine = container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider)
      .reelsEngine as CollapseReelsEngine;

    this.multiplierHolderComponent = container.resolve<SantaMultiplierHolderComponent>(
      SANTA_MULTIPLIER_HOLDER_COMPONENT_TOKEN
    );
    this.slotBackgroundImageComponent = container.resolve<SantaBackgroundImageComponent>(
      BACKGROUND_MODE_COMPONENT_TOKEN
    );

    this.reelSoundModel = this.container.resolve<RegularSpinsSoundModelComponent>(
      T_RegularSpinsSoundModelComponent
    ).regularSpinSoundModel;
    this.resourcesComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);

    this.reelsConfig = container.resolve<IReelsConfigProvider>(T_IReelsConfigProvider).reelsConfig;

    this.trifectaSDKAdapter = container.resolve<TrifectaSDKAdapter>(TRIFECTA_SDK_ADAPTER_TOKEN, {
      optional: true,
    });

    this.gameDataService = container.resolve<GameDataService>(GAME_DATA_SERVICE_TOKEN);

    this.buildPopup();

    if (!Compatibility.IsMobileBrowser) {
      const debouncedResize = debounce(() => this.resize(), 100);

      viewportChangeListener.on(debouncedResize);
      this.gameStateMachine.bonus.entered.listen(() => this.resize());
      this.gameStateMachine.beginFreeSpinsPopup.entered.listen(() => this.resize());
    }

    this.gameStateMachine.bonus.entered.listen(() => this.trifectaSDKAdapter?.hideControlPanel());
    this.gameStateMachine.beginFreeSpinsPopup.leaved.listen(() =>
      this.trifectaSDKAdapter?.showControlPanel()
    );
    this.gameStateMachine.regularSpins.entered.listen(() =>
      this.trifectaSDKAdapter?.showControlPanel()
    );

    this.gameStateMachine.bonus.appendLazyAnimation(() => this.scatterAnimation());
    this.gameStateMachine.bonus.appendLazyAnimation(() => this.scatterFly());
    this.gameStateMachine.bonus.appendLazyAnimation(
      () => new FunctionAction(() => this.gameStateMachine.resume())
    );

    this.gameStateMachine.beginFreeSpinsPopup.appendLazyAnimation(() => this.showScatterPopup());

    this.gameStateMachine.scatter.appendLazyAnimation(
      () => new FunctionAction(() => this.gameStateMachine.resume())
    );

    this.popupScene.scale = new Vector2(0.8, 0.8);
    this.resize();
  }

  private buildPopup(): void {
    const rootScene = this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).root;
    const sceneCommon = this.container.resolve<SceneCommon>(SCENE_COMMON_TOKEN);
    this.popupScene = sceneCommon.sceneFactory.build(SceneNames.Popup)!;
    this.backgroundScene = sceneCommon.sceneFactory.build(SceneNames.BackgroundPopup)!;

    this.backgroundImage = this.backgroundScene.findById(SceneNames.WheelBackground)!;
    const sizeHolder = this.backgroundScene.findById(SceneNames.SizeHolderHorizontal)!;

    this.backgroundScene.z = POPUP_BACKGROUND_DRAW_ORDER;
    this.popupScene.z = POPUP_DRAW_ORDER;
    this.backgroundImage.size = sizeHolder.size.clone();

    rootScene.addChild(this.popupScene);
    rootScene.addChild(this.backgroundScene);
  }

  private scatterAnimation(): IntervalAction {
    const bonusInfo = this.gameStateMachine.curResponse.bonusInfo;
    const scatterPositions = this.getScatterPositions();

    if (!bonusInfo || bonusInfo.bonusFinished || scatterPositions.length === 0) {
      return new EmptyAction();
    }

    const scatterSound = this.reelSoundModel.getSoundByName('scatter_idle');
    const buttonClickSound = this.reelSoundModel.getSoundByName('button_click');

    const clickEventStream = new EventStream<unknown>();

    const flyHolder = this.resourcesComponent.root.findById('flyHolder')!;
    const pickGameIcon = this.resourcesComponent.root.findById('picGame')!;

    this.scatterIcons = [];

    const actions: IntervalAction[] = [];
    const idleActions: IntervalAction[] = [];

    scatterPositions.forEach((position) => {
      const scatterIcon = this.addIconAndHideScatterIcon(position);
      this.scatterIcons.push(scatterIcon);

      flyHolder.addChild(scatterIcon);
    });

    const waitClickAction = new WaitForAction(clickEventStream);

    actions.push(
      new FunctionAction(() => {
        for (let i = 0; i < this.reelsConfig.reelCount; i++) {
          for (let j = 0; j < this.reelsConfig.lineCount; j++) {
            const entity = this.reelsEngine.getStoppedEntities(i, j)[0];
            this.reelsEngine.stopAnimation(entity, 'anim');
          }
        }
        this.multiplierHolderComponent.hide();
        pickGameIcon.stateMachine?.switchToState('pic');
        this.fadeReelsProvider.enableFade(true);
      })
    );

    this.scatterIcons.forEach((scatterIcon) => {
      actions.push(
        new FunctionAction(() => {
          NodeUtils.removeMask(scatterIcon);
          scatterIcon.stateMachine?.switchToState('idle');
          if (scatterSound) {
            scatterSound.stop();
            scatterSound.play();
          }
        })
      );
    });
    idleActions.push(
      new FunctionAction(() => {
        const buttonSubscriptions: EventStreamSubscription<ButtonBase>[] = [];
        const buttons: Button[] = [];
        scatterPositions.forEach((_, index) => {
          const scatterIcon = this.scatterIcons[index];

          const button = new Button();
          flyHolder.addChild(button);

          button.position = scatterIcon.position.clone();

          const symbolSize = this.reelsConfig.symbolSize.clone();

          const touchSize = symbolSize.clone().multiply(scatterIcon.scale.clone());

          button.size = touchSize.clone();
          button.touchArea = new Rect(new Vector2(0, 0), touchSize);

          button.z = scatterIcon.z + 1;

          buttonSubscriptions.push(
            button.clicked.listen(async () => {
              buttonSubscriptions.forEach((subscription) => subscription.cancel());
              buttonSubscriptions.length = 0;

              if (buttonClickSound) {
                buttonClickSound.stop();
                buttonClickSound.play();
              }
              if (scatterSound) {
                scatterSound.stop();
              }

              const isPicked = await this.sendPickScatter(index);

              if (!isPicked) {
                return;
              }

              this.scatterClickedIndex = index;

              clickEventStream.dispatchEvent(index);

              button.parent?.removeChild(button);
              button.deinitialize();
            })
          );

          buttons.push(button);
        });

        if (this.trifectaSDKAdapter?.isReplay) {
          const index = Math.floor(Math.random() * buttons.length);
          setTimeout(() => {
            buttons[index].dispatchClick();
          }, 1000);
        }
      })
    );

    return new SequenceAction([
      new ParallelAction(actions),
      new SequenceAction(idleActions),
      waitClickAction,
      new FunctionAction(() => {
        this.multiplierHolderComponent.show();
      }),
    ]);
  }

  private scatterFly(): IntervalAction {
    if (this.scatterClickedIndex === null) {
      return new EmptyAction();
    }

    const scatterFlySound = this.reelSoundModel.getSoundByName('scatter_fly');

    const animIconsHolder = this.resourcesComponent.root.findById(SceneNames.AnimIconsHolder)!;

    const scatterNode = this.scatterIcons[this.scatterClickedIndex];

    const symbolSize = this.reelsConfig.symbolSize
      .clone()
      .multiply(scatterNode.scale.clone())
      .mulNum(0.5);
    const endPosition = animIconsHolder.size.clone().mulNum(0.5).subtract(symbolSize);

    scatterNode.z = DrawOrderConstants.Top;

    const interpolateAction = new InterpolateInplaceAction<Vector2>((v) => v.clone())
      .withInterpolateFunction(Vector2.lerpInplace)
      .withTimeFunction(easeInQuart)
      .withValues(scatterNode.position.clone(), endPosition)
      .withDuration(1);

    interpolateAction.beginning.listen(() => {
      if (scatterFlySound) {
        scatterFlySound.stop();
        scatterFlySound.play();
      }
    });

    interpolateAction.valueChange.listen((position) => {
      scatterNode.position = position;
    });

    interpolateAction.done.listen(() => {
      scatterNode.visible = false;
    });

    return new ParallelAction([interpolateAction, new EmptyAction().withDuration(1)]);
  }

  private showScatterPopup(): IntervalAction {
    const wheelAnim = this.popupScene.findById(SceneNames.WheelAnim)!;
    const wheel = wheelAnim.findById(SceneNames.Wheel)!;

    const wheelBackgroundAnim = this.backgroundScene.findById(SceneNames.WheelBackgroundAnim)!;

    const buttonContinue = this.popupScene.findById<Button>(SceneNames.ButtonContinue)!;
    const buttonStates = this.popupScene.findById<Button>(SceneNames.ButtonStates)!;

    const waitForButtonContinue = new WaitForAction(buttonContinue.clicked);

    const actions: IntervalAction[] = [];

    const freeSpinsAmount =
      this.gameStateMachine.curResponse.freeSpinsInfo?.currentFreeSpinsGroup?.count;

    const scatterPositions = this.getScatterPositions();

    if (!freeSpinsAmount) {
      return new EmptyAction();
    }

    const stateIndex = freeSpinsAmount / 3 - 1;

    actions.push(
      new FunctionAction(() => {
        wheelAnim.stateMachine?.switchToState(SceneStates.Show);
        wheelBackgroundAnim.stateMachine?.switchToState(SceneStates.Show);
      })
    );
    actions.push(
      new EmptyAction().withDuration(
        wheelAnim.stateMachine?.getStateDuration(SceneStates.Show) ?? 0
      )
    );
    actions.push(
      new FunctionAction(() => {
        wheel.stateMachine?.switchToState(`stop${stateIndex}`);

        if (scatterPositions.length > 3) {
          wheel.stateMachine?.switchToState(SceneStates.On);
        }
      })
    );
    actions.push(
      new EmptyAction().withDuration(wheel.stateMachine?.getStateDuration(`stop${stateIndex}`) ?? 0)
    );
    actions.push(
      new FunctionAction(() => {
        wheelAnim.stateMachine?.switchToState(SceneStates.Popup);
        wheel.stateMachine?.switchToState(SceneStates.Popup);
        this.slotBackgroundImageComponent.setFreeSpins();
      })
    );
    actions.push(
      new EmptyAction().withDuration(wheel.stateMachine?.getStateDuration(SceneStates.Popup) ?? 0)
    );
    actions.push(
      new FunctionAction(() => {
        wheel.stateMachine?.switchToState(`popup${stateIndex}`);
        buttonStates.stateMachine?.switchToState(SceneStates.Show);
      })
    );

    if (this.trifectaSDKAdapter?.isReplay) {
      actions.push(
        new FunctionAction(() => {
          setTimeout(() => {
            waitForButtonContinue.end();
          }, 1000);
        })
      );
    }

    actions.push(
      new FunctionAction(() => {
        wheelAnim.stateMachine?.switchToState(SceneStates.Hide);
        wheel.stateMachine?.switchToState(SceneStates.Off);
        buttonStates.stateMachine?.switchToState(SceneStates.Hide);
        wheelBackgroundAnim.stateMachine?.switchToState(SceneStates.Hide);

        this.scatterIcons.forEach((scatterIcon) => {
          scatterIcon.parent?.removeChild(scatterIcon);
          scatterIcon.deinitialize();
        });

        this.scatterIcons = [];
        this.scatterClickedIndex = null;

        scatterPositions.forEach((position) => {
          const reel = this.reelsEngine.getReelByPosition(position);
          const line = this.reelsEngine.getLineByPosition(position);
          const entity = this.reelsEngine.getReelStoppedEntities(reel, line)[0];

          if (!entity) {
            return;
          }

          const entityIconNode = entity.get<SceneObject>(
            this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.IconNode)
          );
          entityIconNode.visible = true;
        });
      })
    );
    actions.push(
      new EmptyAction().withDuration(
        wheelAnim.stateMachine?.getStateDuration(SceneStates.Hide) ?? 1
      )
    );
    actions.push(
      new FunctionAction(() => {
        this.gameStateMachine.doSpin();
      })
    );

    return new SequenceAction(actions);
  }

  private getScatterPositions(): number[] {
    const response = this.gameStateMachine.curResponse;

    const findScatterWinPosition = (
      winPositions: ReelWinPosition[] | null | undefined
    ): ReelWinPosition | undefined =>
      winPositions?.find((winPosition) => winPosition.type === SCATTER_WIN_POSITION_TYPE);

    const collapseGroups = response.collapseInfo?.groups;
    const lastCollapseGroup = collapseGroups?.length
      ? collapseGroups[collapseGroups.length - 1]
      : null;

    const scatterWinPosition =
      findScatterWinPosition(response.winPositions) ??
      findScatterWinPosition(lastCollapseGroup?.winPositions);

    return scatterWinPosition?.positions ?? [];
  }

  private async sendPickScatter(pickedIndex: number): Promise<boolean> {
    const response = await this.gameDataService.pickBonus({
      pickedButtons: [pickedIndex],
      round: PICK_BONUS_ROUND,
      type: this.gameStateMachine.curResponse.bonusInfo!.type,
    });

    if (response.isFakeResponse) {
      return false;
    }

    this.gameStateMachine.responseHolder.preResponse = response;
    this.gameStateMachine.responseHolder.curResponse = response;

    return true;
  }

  private addIconAndHideScatterIcon(position: number): SceneObject {
    const scatterIcon = this.resourcesComponent.getIcon(`icon_1`)[0];

    const reel = this.reelsEngine.getReelByPosition(position);
    const line = this.reelsEngine.getLineByPosition(position);
    const entity = this.reelsEngine.getReelStoppedEntities(reel, line)[0];
    const entityPosition = entity.get<Vector2>(
      this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.Position)
    );
    const entityIconNode = entity.get<SceneObject>(
      this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.IconNode)
    );

    scatterIcon.position = entityPosition;

    entityIconNode.visible = false;
    return scatterIcon;
  }

  private resize(): void {
    if (!Compatibility.IsMobileBrowser) {
      if (window.innerWidth < window.innerHeight) {
        this.resizeVertical();
        return;
      }
      this.resizeHorizontal();
      return;
    }
  }

  private resizeVertical(): void {
    this.backgroundImage.scale = new Vector2(1, 1);
  }

  private resizeHorizontal(): void {
    const ratio = window.innerHeight / window.innerWidth;

    const imageWidth = this.backgroundImage.size.x;
    const imageHeight = this.backgroundImage.size.y;

    const imageRatio = imageWidth / imageHeight;

    let backgroundScale = 1;
    backgroundScale = 1 / (imageRatio * ratio);

    if (ratio > 0.47) {
      backgroundScale = 1;
    }

    this.backgroundImage.scale = new Vector2(backgroundScale, backgroundScale);
  }

}
